import { FieldPolicy, StoreObject } from '@apollo/client';

type KeyArgs = FieldPolicy['keyArgs'];

interface Page<T> {
  data: Array<T>;
  totalCount: number;
}

type PageCache<T> = Page<T> & {
  ids: Set<string>;
};

interface OffsetPaginationOptions {
  keyArgs?: KeyArgs;
  includeArchived?: boolean;
}

export function offsetPagination<T extends StoreObject>({
  keyArgs = false,
  includeArchived = false,
}: OffsetPaginationOptions = {}): FieldPolicy<PageCache<T>, Page<T>> {
  return {
    keyArgs,
    merge(
      existing: PageCache<T> | undefined,
      incoming: Page<T>,
      { readField },
    ): PageCache<T> {
      const data = existing?.data ? existing.data.slice(0) : [];
      const ids = existing?.ids ?? new Set<string>();

      // ignore records that are already cached
      const incomingData = (incoming?.data ?? []).filter(
        (record) => !ids.has(readField('id', record) as string),
      );

      incomingData.forEach((record) =>
        ids.add(readField('id', record) as string),
      );

      return {
        totalCount: incoming.totalCount,
        data: [...data, ...incomingData],
        ids,
      };
    },
    read(
      existing: PageCache<T> | undefined,
      { readField },
    ): Page<T> | undefined {
      if (!existing) {
        return undefined;
      }

      let { totalCount, data } = existing;

      if (!includeArchived) {
        data = existing.data.filter(
          (record) => !readField('archivedAt', record),
        );
        totalCount = existing.totalCount - (existing.data.length - data.length);
      }

      return {
        totalCount,
        data,
      };
    },
  };
}
